import { environment } from '@utility/app.util';

import { isNil } from 'lodash';

import { Observable, BehaviorSubject, throwError, Subject } from 'rxjs';
import { catchError, finalize, map as rxjs_map, shareReplay, takeUntil, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

import { tranformMeta } from '@lib/meta.interface';
import { ITag, ITagServer } from '@lib/tag.interface';
import { IBaseSource, ISourceServer } from '@lib/sources.interface';
import { IUser, IUserGroup, IUserGroupsDataServer } from '@lib/users.interface';
import { SITE_ADMINISTRATOR_GROUP_NAME } from './model-constants/roles-permissions.consts';
import { IRole, IRolePayload, IRoleServer, IRoleWithPagination, IRoleWithPaginationServer } from '@lib/roles.interface';
import { Role, Permission, Member, Restriction, IAdminMembersQueryParams } from './model-constants/roles-permissions.interface';

const API_URL: string = `${environment.apiUrl}/v2`;

@Injectable({
  providedIn: 'root',
})
export class RolesPermissionsService {
  static defaultRole: IRole;
  static currentSeletedRoleId: number;
  private static defaultRoleRequest$: Observable<IRole> | null = null;

  private cancelMembersRequest$: Subject<void> = new Subject<void>();
  private rolesListSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public rolesList$: Observable<any> = this.rolesListSubject.asObservable();

  constructor(private http: HttpClient) {}

  public loadDefaultRole(): void {
    if (!isNil(RolesPermissionsService.defaultRole?.id) || RolesPermissionsService.defaultRoleRequest$) {
      return;
    }

    RolesPermissionsService.defaultRoleRequest$ = this.getDefaultRole().pipe(
      finalize((): void => {
        RolesPermissionsService.defaultRoleRequest$ = null;
      }),
      shareReplay(1),
    );

    RolesPermissionsService.defaultRoleRequest$.subscribe({
      next: (val: IRole) => {
        if (isNil(val?.id)) {
          console.error('Could not get default role');
          return;
        }
        RolesPermissionsService.defaultRole = val;
      },
    });
  }

  public getDefaultRole(): Observable<IRole> {
    let params = new HttpParams();
    const keyword = 'site admin';
    params = params.append('filter[is_default]', `true`);
    return this.http.get<IRoleWithPaginationServer>(`${API_URL}/roles`, { params }).pipe(
      rxjs_map((res: IRoleWithPaginationServer): IRole => {
        return this.transformRole(res?.data?.find((role: IRoleServer): boolean => role.is_default));
      })
    );
  }

  public fetchAndSetAllRoles(payload?: IRolePayload): Observable<IRoleWithPagination> {
    const { page = 1, keyword, sort_by, pagination, sort_order, usergroups } = payload || {};

    let params = new HttpParams();
    params = params.append('page', page);
    if (keyword) params = params.append('filter[name]', `${keyword}`);
    if (pagination) params = params.append('pagination', `${pagination}`);
    if (usergroups) params = params.append('filter[usergroup_ids]', `${usergroups}`);
    if (sort_by) params = params.append('sort', `${sort_order === 'desc' ? '-' : ''}${sort_by}`);

    return this.http.get<IRoleWithPaginationServer>(`${API_URL}/roles`, { params }).pipe(
      rxjs_map((response: IRoleWithPaginationServer): IRoleWithPagination => this.transformRolesList(response)),
      tap((response) => this.rolesListSubject.next(response))
    );
  }

  private transformRolesList(response: IRoleWithPaginationServer): IRoleWithPagination {
    return {
      meta: tranformMeta(response.meta),
      data: response.data.map((role: IRoleServer): IRole => this.transformRole(role)),
    };
  }

  private transformRole(role: IRoleServer): IRole {
    const members: IBaseSource[] = role?.members
    .filter((member: IUser): boolean => !!member.id)
    .map((member: IUser): { id: number; title: string } => ({
      id: member.id,
      title: `${member?.first_name} ${member?.last_name}`,
    }));

    const sources: IBaseSource[] = role?.restrictable?.sources
    .filter((source: ISourceServer): boolean => !!source.id)
    .map((source: ISourceServer): IBaseSource => ({
      id: source.id,
      title: source?.title,
    }));

    const usergroups: IBaseSource[] = role?.restrictable?.usergroups
    .filter((usergroup: IUserGroupsDataServer): boolean => !!usergroup.id)
    .map((usergroup: IUserGroupsDataServer): IUserGroup => ({
      id: usergroup.id,
      title: usergroup?.title,
    }));

    const tags: ITag[] = role?.restrictable?.tags
    .filter((tag: ITagServer): boolean => !!tag.id)
    .map((tag: ITagServer): ITag => ({
      ...tag,
      title: tag?.tag_name,
      tagName: tag?.tag_name,
      postCount: tag.post_count || 0,
      mediaCount: tag.media_count || 0,
      contentCount: tag.content_count || 0,
      usergroupCount: tag.usergroup_count || 0,
      totalPostCount: tag.total_post_count || 0,
      usersUnsubscribed: tag.users_unsubscribed || 0,
    }));

    return {
      id: role?.id,
      title: role?.name,
      name: role?.name,
      tags: tags || [],
      members: members || [],
      sources: sources || [],
      permissions: role?.permissions,
      restrictable: {
        tags: tags,
        sources: sources,
        usergroups: usergroups,
      },
      isDefault: role?.is_default,
      usergroups: usergroups || [],
      permissionCount: role?.permissions_count,
    };
  }

  public getRoleById(roleId: number = RolesPermissionsService.currentSeletedRoleId): Observable<Role> {
    return this.http.get<Role>(`${API_URL}/roles/${roleId}`);
  }

  public createRole(roleData: any): Observable<any> {
    return this.http.post<any>(`${API_URL}/roles`, roleData).pipe(
      tap((addedRole): void => {
        const currentRoles = [...(this.rolesListSubject?.value?.data || [])];
        currentRoles.unshift(addedRole.data);
        this.rolesListSubject.next({
          ...this.rolesListSubject.value,
          data: currentRoles,
        });
      }),
      catchError(this.handleError)
    );
  }

  public deleteRole(roleId: number, moveMembersToRoleId?: number): Observable<any> {
    return this.http
      .delete(`${API_URL}/roles`, {
        body: {
          role_ids: [roleId],
          ...(!isNil(moveMembersToRoleId) && {
            target_role_id: moveMembersToRoleId,
          }),
        },
      })
      .pipe(
        tap(() => {
          const currentRoles = this.rolesListSubject.value;
          const updatedRoles = currentRoles.data.filter((role) => role.id !== roleId);
          this.rolesListSubject.next({ ...currentRoles, data: updatedRoles });
        }),
        catchError(this.handleError)
      );
  }

  public deleteRoles(roleIds: number[], moveMembersToRoleId?: number): Observable<any> {
    return this.http
      .delete(`${API_URL}/roles`, {
        body: {
          role_ids: roleIds,
          ...(!isNil(moveMembersToRoleId) && {
            target_role_id: moveMembersToRoleId,
          }),
        },
      })
      .pipe(
        tap(() => {}),
        catchError(this.handleError)
      );
  }

  public editRole(payload: any, roleId: number): Observable<any> {
    return this.http.put<any>(`${API_URL}/roles/${roleId}`, payload).pipe(
      tap((updatedRole) => {
        const currentRoles = this.rolesListSubject.value;
        const updatedRoles = currentRoles.data.map((role) => (role.id === updatedRole.id ? updatedRole : role));
        this.rolesListSubject.next({ ...currentRoles, data: updatedRoles });
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse | string) {
    console.error(error);
    // Rethrown as the HTTP interceptor handed it on: a 400/403 arrives as its message alone (nothing was
    // shown for it), a 422 as the body it already reported. Wrapping it in an Error lost both.
    return throwError(() => error);
  }

  public cancelPreviousGetMembersRequest() {
    this.cancelMembersRequest$.next();
  }

  // Update Role Members
  public updateRoleMembers(membersId: number[], roleId: number, type: 'add' | 'remove'): Observable<any> {
    return this.http
      .put<any>(`${API_URL}/roles/${roleId}/members`, {
        member_ids: membersId,
      })
      .pipe(takeUntil(this.cancelMembersRequest$));
  }

  public assignMembers(roleId: number, membersIds: number[]): Observable<any[]> {
    const body = { member_ids: membersIds };
    return this.http.put<any>(`${API_URL}/roles/${roleId}/members`, body).pipe(takeUntil(this.cancelMembersRequest$));
  }

  // Get Entities By Role Id
  public etMembersByRole(roleId: number = RolesPermissionsService.currentSeletedRoleId): Observable<Member[]> {
    return this.http.get<Member[]>(`${API_URL}/roles/${roleId}/members`);
  }

  public getRestrictionsByRole(roleId: number = RolesPermissionsService.currentSeletedRoleId): Observable<Restriction[]> {
    return this.http.get<Restriction[]>(`${API_URL}/roles/${roleId}/restrictions`);
  }

  public getPermissionsByRole(roleId: number = RolesPermissionsService.currentSeletedRoleId): Observable<Permission[]> {
    return this.http.get<any[]>(`${API_URL}/roles/permissions/${roleId}`);
  }

  public getAllPermissions(): Observable<Permission[]> {
    return this.http
      .get<{ data }>(`${API_URL}/roles/permissions/list`)
      .pipe(rxjs_map((res) => this.transfromAllPermsission(res.data)));
  }

  private transfromAllPermsission(res: any[]): any[] {
    return res
      .filter((removeSiteAdministratorsGroup) => removeSiteAdministratorsGroup.name !== SITE_ADMINISTRATOR_GROUP_NAME)
      .map((group) => ({
        id: group.id,
        name: group.name,
        enabled: false,
        permissions: group.permissions.map((permission) => ({
          id: permission.id,
          name: permission.name,
          groupId: permission.group_id,
          enabled: false,
        })),
      }));
  }

  public getAdminMembers(page: number, per_page: number, search?: string): Observable<any> {
    let params = new HttpParams();

    params = params.append('page', `${page}`);
    params = params.append('limit', `${per_page}`);
    params = params.append('filter[with_roles]', 'true');
    if (search) params = params.append('input', `${search}`);
    return this.http.get(`${API_URL}/users`, { params });
  }

  public getRoleMembers(roleId: number = RolesPermissionsService.currentSeletedRoleId): Observable<any> {
    return this.http.get(`${API_URL}/users`);
  }

  public getMembers(
    search?: string,
    from?: string,
    roleId: number = RolesPermissionsService.currentSeletedRoleId
  ): Observable<any> {
    if (roleId === undefined) return this.http.get(`${API_URL}/users`, { params: { search, from } });
    else return this.http.get(`${API_URL}/roles/${roleId}/members`, { params: { search, from } });
  }

  public getAdminUsers(data?: IAdminMembersQueryParams): Observable<any> {
    let params = new HttpParams();
    const { page = 1, per_page = 10, key, isActive = true, assignedToRole } = data;

    params = params.append('page', `${page}`);
    params = params.append('limit', `${per_page}`);
    // params = params.append('filter[is_active]', `${isActive ? 1 : 0}`);
    params = params.append('filter[user_type_id]', `2`);
    if (key) params = params.append('search', `${key}`);
    if (!isNil(assignedToRole)) params = params.append('filter[role_id', `${assignedToRole}`);
    return this.http.get<any>(`${API_URL}/users`, { params: params }).pipe(
      // takeUntil(this.cancelGetAdminUserRequest$),
      rxjs_map((response: any): any => this.tranformAdminList(response))
    );
  }

  private tranformAdminList(res): any {
    return {
      data: res.data.map((member) => ({
        id: member.id,
        name: `${member.first_name} ${member.last_name}`,
        role: member.roles[0] || null,
        email: member.email,
      })),
      meta: { ...res.meta },
      links: { ...res.links },
    };
  }
}
